import { useEffect, useState } from "react";

import { api } from "~/api";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { CommitPopover } from "~/components/CommitPopover";
import { SessionExpired } from "~/components/SessionExpired";
import { useAuth } from "~/contexts/auth";
import type { VersionGateDetails } from "~/types";
import { formatGitSha } from "~/utils";

const COMMIT_URL = "https://github.com/turbopuffer/turbopuffer/commit";

export default function VersionGatesPage() {
  const [gates, setGates] = useState<VersionGateDetails[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { status, setIsAuthorized } = useAuth();

  useEffect(() => {
    api
      .getVersionGates()
      .then(setGates)
      .catch((err) => {
        if (err.status === 401) setIsAuthorized(false);
        else setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [setIsAuthorized]);

  if (status !== "authorized") return <SessionExpired />;
  if (error) return <div className="text-red-500">Error: {error}</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Version Gates</CardTitle>
      </CardHeader>
      <CardContent>
        <VersionGatesTable gates={gates} loading={loading} />
      </CardContent>
    </Card>
  );
}

function VersionGatesTable({
  gates,
  loading,
}: {
  gates: VersionGateDetails[];
  loading: boolean;
}) {
  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (gates.length === 0) {
    return <p className="text-sm text-muted-foreground">No version gates.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-2 pr-4 font-medium">Gate</th>
            <th className="py-2 pr-4 font-medium">Path</th>
            <th className="py-2 pr-4 font-medium">Scope</th>
            <th className="py-2 font-medium">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {gates.map((gate) => (
            <VersionGateRow key={gate.name} gate={gate} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VersionGateRow({ gate }: { gate: VersionGateDetails }) {
  return (
    <tr>
      <td className="py-3 pr-4 font-mono">
        <a className="hover:underline" href={gate.tag_url}>
          {gate.name}
        </a>
      </td>
      <td className="whitespace-nowrap py-3 pr-4 font-mono">
        <CommitSha commit={gate.required_min_commit} />
        <span className="px-2 text-xs text-muted-foreground">→</span>
        <CommitSha commit={gate.target_commit} align="end" />
      </td>
      <td className="py-3 pr-4 font-mono">{gate.scope}</td>
      <td className="py-3">{gate.description}</td>
    </tr>
  );
}

function CommitSha({
  commit,
  align,
}: {
  commit: VersionGateDetails["required_min_commit"];
  align?: "start" | "end";
}) {
  return (
    <CommitPopover commit={commit} align={align}>
      <a className="hover:underline" href={`${COMMIT_URL}/${commit.sha}`}>
        {formatGitSha(commit.sha)}
      </a>
    </CommitPopover>
  );
}
